/**
 * Mongoose keeps keys that are not in the schema in `_doc`, so they surface via `.lean()` and
 * `toJSON()`. Schemas call {@link excludeLegacyPaths} from a `@pre` query hook to project them away.
 * `.aggregate()` bypasses query middleware and still sees them.
 */
export type LegacyProjectionQuery = {
    projection: () => Record<string, unknown> | null | undefined;
    select: (arg: Record<string, number>) => unknown;
};

/**
 * MongoDB rejects mixing inclusion and exclusion in one projection (`_id` is the sole
 * exception), so exclusions can only be appended to queries that aren't already
 * inclusion-projected. Those pass through unfiltered: one that includes a legacy path's parent
 * (`{ topLeft: 1 }`) returns the legacy child with it.
 */
function hasInclusionProjection(projection: Record<string, unknown> | null | undefined): boolean {
    if (!projection) return false;
    return Object.entries(projection).some(([field, value]) => field !== "_id" && (value === 1 || value === true));
}

/**
 * A projection may not contain both a path and a prefix of that path — MongoDB rejects it with
 * "Path collision" (Location31249). A caller excluding a whole parent (RefreshSensingPeriods does
 * `sproutNetworkInfo: 0`) therefore collides with any child of it in the legacy list, so paths that
 * the caller has already covered are dropped instead of merged.
 */
function collidesWithCallerPath(legacyPath: string, callerPaths: string[]): boolean {
    return callerPaths.some(
        (callerPath) =>
            callerPath === legacyPath ||
            legacyPath.startsWith(`${callerPath}.`) ||
            callerPath.startsWith(`${legacyPath}.`),
    );
}

export function excludeLegacyPaths(query: LegacyProjectionQuery, paths: readonly string[]): void {
    const projection = query.projection();
    if (hasInclusionProjection(projection)) return;

    const callerPaths = Object.keys(projection ?? {});
    const exclusion = Object.fromEntries(
        paths.filter((path) => !collidesWithCallerPath(path, callerPaths)).map((path) => [path, 0]),
    );
    if (Object.keys(exclusion).length === 0) return;
    query.select(exclusion);
}
